import { usePlaceholderHook } from "@/hooks/use-placeholder.hook.ts";
import { CircleInfo } from "@/icons/CircleInfo.tsx";
import { Clone } from "@/icons/Clone.tsx";
import { MicrochipAI } from "@/icons/MicrochipAI.tsx";
import api from "@/lib/api.ts";
import { AspectRatio, Outputs } from "@/lib/helpers.ts";
import { LoraModel } from "@/models/lora.model.ts";
import { TemplateModel } from "@/models/template.model.ts";
import { getLoras } from "@/services/lora.service.ts";
import { AspectRatioSelect } from "@/ui/shared/AspectRatioSelect.tsx";
import classes from "@/ui/shared/StyleSelect.module.css";
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Image,
  Modal,
  Paper,
  SegmentedControl,
  Text,
  Tooltip,
} from "@mantine/core";
import { useDisclosure, useLocalStorage } from "@mantine/hooks";
import { useMutation, useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { memo, useState } from "react";
import { useNavigate } from "react-router";

type TemplateCardProps = {
  template: TemplateModel;
};

export const TemplateCard = memo(function TemplateCard(
  props: TemplateCardProps,
) {
  const { template } = props;
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const placeholder = usePlaceholderHook();
  const [outputs, setOutputs] = useState<Outputs>("4");
  const [aspectRatio, setAspectRatio] = useLocalStorage<AspectRatio>({
    key: "aspect-ratio",
    defaultValue: "1:1",
  });
  const [lora, setLora] = useState<LoraModel | null>();
  const [activeTemplateId, setActiveTemplateId] = useState<number | null>(null);
  const [templateOpened, { open: openTemplate, close: closeTemplate }] =
    useDisclosure(false);

  const loraParams = {
    limit: 0,
    sort: "-created_at",
    "filter[status]": "published",
  };
  const { data: loraData } = useQuery({
    queryKey: ["loras", loraParams],
    queryFn: () => getLoras(loraParams),
    retry: 1,
  });
  const loras: LoraModel[] = loraData?.data || [];

  const templateMutation = useMutation({
    mutationFn: () => {
      return api.post(`/templates/${activeTemplateId}/replicate`, {
        app: "playground",
        outputs,
        lora_id: lora?.id,
        aspect_ratio: aspectRatio,
      });
    },
    onSuccess: () => {
      closeTemplate();
      navigate("/playground");
    },
  });

  return (
    <>
      <Paper
        onClick={() => {
          openTemplate();
          setLora(null);
          setOutputs("4");
          setActiveTemplateId(template.id);
        }}
        withBorder
        className="relative group rounded-lg overflow-hidden hover:scale-[1.025] transition-all duration-300 cursor-pointer"
      >
        {isLoading && <Image src={placeholder} />}
        <img
          className={`object-contain ${isLoading ? "hidden" : "block"}`}
          src={template.url}
          alt={template.name}
          onLoad={() => setIsLoading(false)}
        />

        <div className="absolute bottom-0 left-0 px-4 py-3 w-full flex flex-col gap-2 bg-gray-950/75">
          <Text c="white">{template.name}</Text>

          {template.tags && template.tags?.data.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {template.tags?.data.map((tag) => (
                <Badge size="sm" variant="default" key={tag.name}>
                  {tag.name}
                </Badge>
              ))}
            </div>
          )}
        </div>

        <div className="invisible group-hover:visible duration-500 transition-opacity ease-in-out absolute w-full h-full inset-0 bg-gray-900/75"></div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileHover={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="absolute w-full h-full inset-0 flex flex-col gap-3 items-center justify-center"
        >
          <Clone color="grape" size={50} />
          <Text>Replicate photo!</Text>
        </motion.div>
      </Paper>

      <Modal
        opened={templateOpened}
        onClose={() => {
          closeTemplate();
        }}
        title="Generate AI photo"
      >
        <div className="flex flex-col gap-4">
          {loras?.length === 0 && (
            <Alert
              bd="1px dashed yellow.6"
              icon={<CircleInfo size={20} />}
              color="orange"
            >
              Train an AI character before generating photos using this image as
              a reference!
            </Alert>
          )}

          {loras?.length > 0 && (
            <>
              <Alert
                bd="1px dashed violet.6"
                icon={<CircleInfo size={20} />}
                color="violet"
              >
                Generate an AI photo using this image as a reference!
              </Alert>

              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-0.5">
                  <Text fw={500} size="md" component="label" mb={0}>
                    Character
                  </Text>

                  <Card withBorder={true}>
                    <div className="flex flex-wrap gap-2 items-center justify-center">
                      {loras.map((loraEntry) => (
                        <button
                          type="button"
                          key={loraEntry.id}
                          onClick={() => {
                            if (loraEntry.id === lora?.id) {
                              setLora(undefined);
                              return;
                            }
                            setLora(loraEntry);
                          }}
                          className="flex flex-col items-center p-1 relative cursor-pointer text-sm"
                        >
                          {loraEntry.id === lora?.id && (
                            <div className={classes.indicator} />
                          )}
                          <Image
                            className="z-[2]"
                            src={loraEntry.cover || placeholder}
                            radius="sm"
                            w={80}
                            h={80}
                          />

                          {loraEntry.id === lora?.id && (
                            <span className={classes.selected}>
                              {loraEntry.name}
                            </span>
                          )}
                          {loraEntry.id !== lora?.id && (
                            <span className={classes.deselected}>
                              {loraEntry.name}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </Card>
                </div>

                <AspectRatioSelect
                  value={aspectRatio}
                  setValue={setAspectRatio}
                />

                <div className="flex flex-col gap-0.5">
                  <Text fw={500} size="md" component="label" mb={0}>
                    Number of variations
                  </Text>

                  <SegmentedControl
                    color="violet"
                    value={outputs}
                    onChange={(value) => setOutputs(value as Outputs)}
                    data={[
                      {
                        label: "1",
                        value: "1",
                      },
                      {
                        label: "2",
                        value: "2",
                      },
                      {
                        label: "3",
                        value: "3",
                      },
                      {
                        label: "4",
                        value: "4",
                      },
                    ]}
                  />
                </div>
              </div>

              <Group justify="end">
                <Button
                  variant="subtle"
                  color="gray"
                  onClick={() => {
                    closeTemplate();
                  }}
                >
                  Cancel
                </Button>

                <Tooltip
                  withArrow={true}
                  position="bottom"
                  label={`${+outputs * 10} credits`}
                >
                  <Button
                    variant="gradient"
                    gradient={{ from: "grape", to: "violet", deg: 90 }}
                    onClick={() => {
                      templateMutation.mutate();
                    }}
                    leftSection={<MicrochipAI />}
                    loading={templateMutation.isPending}
                    disabled={templateMutation.isPending || !lora}
                  >
                    Generate
                  </Button>
                </Tooltip>
              </Group>
            </>
          )}
        </div>
      </Modal>
    </>
  );
});
